import React from "react";
import { Link } from "react-router-dom";
import { useLenis } from "lenis/react";
import { Instagram, Linkedin, Facebook } from "lucide-react";
import { FOOTER_LINKS } from "../../config/navigation";
import { COMPANY } from "../../config/company";
import { useLanguage } from "../../context/LanguageContext";

export default function Footer() {
  const { t } = useLanguage();
  const lenis = useLenis();

  const goTo = (href) => {
    const el = document.querySelector(href);
    if (el && lenis) lenis.scrollTo(el, { offset: -72 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.assign(`/${href}`); // anchor lives on the home page
  };

  // Social icons render only when a real profile URL is configured.
  const socials = [
    { Icon: Instagram, key: "ig", url: COMPANY.instagramUrl },
    { Icon: Linkedin, key: "li", url: COMPANY.linkedinUrl },
    { Icon: Facebook, key: "fb", url: COMPANY.facebookUrl },
  ].filter((s) => s.url);

  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#0D0D0D] text-[#F6F2EC]" data-testid="site-footer">
      <div className="container-tessa py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <img
              src="/tessa-logo-light.png"
              alt="TESSA Corporation"
              className="h-9 md:h-10 w-auto select-none"
              draggable="false"
            />
            <p className="mt-6 max-w-sm text-[#F6F2EC]/70 leading-relaxed text-[15px]">
              {t("footer.description")}
            </p>
            <div className="mt-8 flex items-center gap-3">
              {socials.map(({ Icon, key, url }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`footer-social-${key}`}
                  aria-label={`TESSA ${key}`}
                  className="grid place-items-center h-10 w-10 border border-white/15 hover:border-[#C4A46B] hover:text-[#C4A46B] transition-colors"
                >
                  <Icon size={17} strokeWidth={1.6} />
                </a>
              ))}
            </div>
          </div>

          {/* Explore */}
          <div className="md:col-span-3 md:col-start-7">
            <h4 className="font-sans text-[12px] uppercase tracking-[0.2em] text-[#C4A46B] mb-5">
              {t("footer.explore")}
            </h4>
            <ul className="space-y-3">
              {FOOTER_LINKS.explore.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => goTo(l.href)}
                    data-testid={`footer-link-${l.href.replace("#", "")}`}
                    className="text-[15px] text-[#F6F2EC]/75 hover:text-[#F6F2EC] link-underline"
                  >
                    {t(l.labelKey)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="md:col-span-3">
            <h4 className="font-sans text-[12px] uppercase tracking-[0.2em] text-[#C4A46B] mb-5">
              {t("footer.company")}
            </h4>
            <ul className="space-y-3">
              {FOOTER_LINKS.company.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    data-testid={`footer-link-${l.href.replace(/[#/]/g, "")}`}
                    className="text-[15px] text-[#F6F2EC]/75 hover:text-[#F6F2EC] link-underline"
                  >
                    {t(l.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
  <p className="font-serif italic text-xl text-[#D9A9AE]">
    {t("footer.grown")}
  </p>

  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
    <p className="text-[13px] text-[#F6F2EC]/50">
      © {year} {COMPANY.fullName}. {t("footer.rights")}
    </p>

    <span
      className="hidden sm:block text-[#F6F2EC]/20"
      aria-hidden="true"
    >
      |
    </span>

    <a
      href="https://www.emprenovus.com"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="footer-emprenovus-link"
      className="group inline-flex items-center gap-2 text-[13px] text-[#F6F2EC]/60 hover:text-[#C4A46B] transition-colors duration-300"
    >
      <img
        src="/Emprenovus.png"
        alt="Emprenovus"
        className="w-4 h-4 object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-300"
      />

      <span>
        AI powered by Emprenovus
      </span>
    </a>
  </div>
</div>
      </div>
    </footer>
  );
}
